import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Star } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { Media } from "./Media";
import { imgUrl } from "@/lib/content";

const matches = (pages, path) =>
  (pages || "all").split(",").map((p) => p.trim()).some((p) => p === "all" || p === path || (p.endsWith("*") && path.startsWith(p.slice(0, -1))));

const seenRecently = (p) => {
  if (localStorage.getItem(`wm_pop_done_${p.id}`)) return true;
  if (p.frequency_days === 0) return !!sessionStorage.getItem(`wm_pop_${p.id}`);
  const t = Number(localStorage.getItem(`wm_pop_${p.id}`) || 0);
  return Date.now() - t < p.frequency_days * 86400000;
};

const markSeen = (p) => {
  localStorage.setItem(`wm_pop_${p.id}`, String(Date.now()));
  sessionStorage.setItem(`wm_pop_${p.id}`, "1");
};

const siteStart = () => {
  let s = Number(sessionStorage.getItem("wm_site_start"));
  if (!s) { s = Date.now(); sessionStorage.setItem("wm_site_start", String(s)); }
  return s;
};

const PopupForm = ({ p, onDone }) => {
  const [f, setF] = useState({ name: "", email: "", birthday: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/subscribers", { popup_id: p.id, ...f });
      localStorage.setItem(`wm_pop_done_${p.id}`, "1");
      setMsg(data.message);
      setTimeout(onDone, 2200);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (msg) return <p className="pop-success" data-testid="popup-success">{msg}</p>;
  return (
    <form className="pop-form" onSubmit={submit} data-testid="popup-form">
      <input required placeholder="Your name" autoComplete="name" value={f.name} onChange={set("name")} data-testid="popup-name-input" />
      <input required type="email" inputMode="email" placeholder="Email address" autoComplete="email" value={f.email} onChange={set("email")} data-testid="popup-email-input" />
      {p.collect_birthday && (
        <label className="pop-bday">Birthday
          <input required type="date" value={f.birthday} onChange={set("birthday")} data-testid="popup-birthday-input" />
        </label>
      )}
      {error && <p className="form-error" data-testid="popup-error">{error}</p>}
      <button className="btn gold" type="submit" disabled={busy} data-testid="popup-submit-button">
        {busy ? <Loader2 size={16} className="spin" /> : p.button_label}
      </button>
    </form>
  );
};

const ImagePopup = ({ p, onClose }) => {
  const navigate = useNavigate();
  const img = (
    <picture>
      {p.image_mobile && <source media="(max-width: 600px)" srcSet={imgUrl(p.image_mobile, 900)} />}
      <img src={imgUrl(p.image, 1400)} alt={p.title || p.name || "Promotion"} data-testid="popup-promo-image" />
    </picture>
  );
  const go = (e) => {
    const url = p.link_url.trim();
    api.post(`/popups/${p.id}/click`).catch(() => {});
    onClose();
    if (/^https?:\/\//.test(url)) return;
    e.preventDefault();
    if (url.startsWith("#")) {
      if (window.location.pathname !== "/") navigate(`/${url}`);
      else document.querySelector(url)?.scrollIntoView({ behavior: "smooth" });
    } else navigate(url);
  };
  return (
    <DialogContent className="popup popup-image" data-testid="site-popup">
      <DialogTitle className="sr-only">{p.title || p.name || "Promotion"}</DialogTitle>
      <DialogDescription className="sr-only">Promotion</DialogDescription>
      {p.link_url ? (
        <a href={p.link_url} onClick={go} target={/^https?:/.test(p.link_url) ? "_blank" : undefined} rel="noreferrer" data-testid="popup-promo-link">{img}</a>
      ) : img}
    </DialogContent>
  );
};

export const PopupManager = () => {
  const { pathname } = useLocation();
  const [active, setActive] = useState(null);
  const skip = pathname.startsWith("/admin") || pathname.startsWith("/staff") || pathname.startsWith("/pay");
  const { data } = useQuery({ queryKey: ["popups"], queryFn: async () => (await api.get("/popups")).data, enabled: !skip, staleTime: 10 * 60_000 });

  useEffect(() => {
    if (skip || !data || active) return undefined;
    const timers = data.filter((p) => matches(p.pages, pathname) && !seenRecently(p)).map((p) => {
      const elapsed = p.trigger === "site_time" ? (Date.now() - siteStart()) / 1000 : 0;
      return setTimeout(() => {
        if (document.querySelector("[role=dialog]")) return;
        setActive((cur) => cur || p);
      }, Math.max(0, p.seconds - elapsed) * 1000);
    });
    return () => timers.forEach(clearTimeout);
  }, [data, pathname, skip, active]);

  useEffect(() => {
    if (!active) return;
    markSeen(active);
    api.post(`/popups/${active.id}/view`).catch(() => {});
  }, [active]);

  if (!active) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && setActive(null)}>
      {active.kind === "image" ? <ImagePopup p={active} onClose={() => setActive(null)} /> : (
      <DialogContent className="popup" data-testid="site-popup">
        {active.image && <Media className="pop-img" src={active.image} w={800} priority />}
        <div className="pop-inner">
          <p className="eyebrow pop-eyebrow"><Star size={11} className="gold-ic" /> {active.list_name}</p>
          <DialogTitle className="pop-title" data-testid="popup-title">{active.title}</DialogTitle>
          <DialogDescription className="pop-body">{active.body}</DialogDescription>
          <PopupForm p={active} onDone={() => setActive(null)} />
        </div>
      </DialogContent>
      )}
    </Dialog>
  );
};
